import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  MessageCircle, 
  Mail, 
  Book, 
  HelpCircle,
  ExternalLink,
  Send,
  FileText,
  Users,
  Lightbulb
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export function HelpSupport({ userId }: { userId: string }) {
  const [supportForm, setSupportForm] = useState({
    subject: '',
    message: '',
    category: 'general'
  });
  const [sending, setSending] = useState(false);

  const handleSubmitSupport = async () => {
    if (!supportForm.subject || !supportForm.message) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setSending(true);
      
      // Log support request
      await supabase.rpc('log_therapy_action', {
        p_user_id: userId,
        p_action: 'support_request_submitted',
        p_details: {
          subject: supportForm.subject,
          category: supportForm.category,
          timestamp: new Date().toISOString()
        }
      });

      toast.success('Support request submitted. We\'ll get back to you soon!');
      setSupportForm({ subject: '', message: '', category: 'general' });
    } catch (error) {
      console.error('Error submitting support request:', error);
      toast.error('Failed to submit support request');
    } finally {
      setSending(false);
    }
  };

  const faqs = [
    {
      question: 'How do Therappi® AI credits work?',
      answer: 'Credits are used for Therappi® AI therapy sessions. Subscribers receive monthly credits that refresh automatically. You can also purchase additional credit packs as needed.'
    },
    {
      question: 'Is my data secure and private?',
      answer: 'Yes. We use end-to-end encryption for all Therappi® AI conversations. Your data is stored securely and never shared with third parties. You can export or delete your data at any time.'
    },
    {
      question: 'How do I cancel my subscription?',
      answer: 'You can cancel anytime from the Subscription tab in your account settings. Click "Manage Subscription" to access the Stripe portal where you can cancel or modify your plan.'
    },
    {
      question: 'What happens if I run out of credits?',
      answer: 'When you run out of credits, you can purchase additional credit packs or upgrade to a higher tier subscription with more monthly credits.'
    },
    {
      question: 'Can I use Therappi® on mobile?',
      answer: 'Yes! Therappi® has native iOS and Android apps with full feature support including offline access and push notifications.'
    },
  ];

  const helpResources = [
    {
      icon: Book,
      title: 'Documentation',
      description: 'Comprehensive guides and tutorials',
      link: '/docs'
    },
    {
      icon: FileText,
      title: 'Privacy Policy',
      description: 'How we protect your data',
      link: '/privacy'
    },
    {
      icon: FileText,
      title: 'Terms of Service',
      description: 'Usage terms and conditions',
      link: '/terms'
    },
    {
      icon: Users,
      title: 'Community Guidelines',
      description: 'Best practices for wellness',
      link: '/community'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Quick Help Resources */}
      <div className="grid gap-4 md:grid-cols-2">
        {helpResources.map((resource, index) => (
          <Card key={index} className="hover:bg-accent/50 transition-colors cursor-pointer">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="p-2 rounded-lg bg-primary/10">
                <resource.icon className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">{resource.title}</h3>
                <p className="text-sm text-muted-foreground">{resource.description}</p>
              </div>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            Frequently Asked Questions
          </CardTitle>
          <CardDescription>Find quick answers to common questions</CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Contact Support
          </CardTitle>
          <CardDescription>
            Can't find what you're looking for? Send us a message
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={supportForm.subject}
              onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
              placeholder="What can we help you with?"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={supportForm.message}
              onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
              placeholder="Please describe your issue or question..."
              rows={6}
            />
          </div>

          <Button 
            onClick={handleSubmitSupport} 
            disabled={sending}
            className="w-full"
          >
            {sending ? (
              'Sending...'
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Message
              </>
            )}
          </Button>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4" />
            <span>Or email us directly at support@therappi.app</span>
          </div>
        </CardContent>
      </Card>

      {/* Feature Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Feature Requests
          </CardTitle>
          <CardDescription>
            Have an idea to improve Therappi®? We'd love to hear it!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Your feedback helps us prioritise new features and improvements. Share your ideas and vote on existing requests.
          </p>
          <Button variant="outline" className="w-full">
            <ExternalLink className="mr-2 h-4 w-4" />
            Visit Feature Request Portal
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
